// File: webelieve-frontend/src/features/intake/components/LeadInboxView.tsx
// EF-7F: Unified lead inbox — aggregates all inquiry sources

import React, { useState, useEffect, useCallback } from 'react';
import { intakeInboxApi, type InboxItem, type InboxFilters, type InboxResult, type InboxStats } from '../api/intakeInboxApi';
import type { LeadSummary } from '../api/intakeCrmApi';

interface LeadInboxViewProps {
  onLeadClick: (lead: LeadSummary) => void;
}

const SOURCE_COLORS: Record<string, string> = {
  Website: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Email: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  Phone: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Referral: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  'Walk-in': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
  'Google Ads': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  Social: 'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  Other: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'text-red-600 dark:text-red-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-gray-500 dark:text-gray-400',
};

const LeadInboxView: React.FC<LeadInboxViewProps> = ({ onLeadClick }) => {
  const [inbox, setInbox] = useState<InboxResult | null>(null);
  const [stats, setStats] = useState<InboxStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const loadInbox = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: InboxFilters = {
        limit: 100,
      };
      if (sourceFilter) filters.source = sourceFilter;
      if (searchQuery) filters.search = searchQuery;
      if (showUnreadOnly) filters.is_read = false;

      const [inboxData, statsData] = await Promise.all([
        intakeInboxApi.getInbox(filters),
        intakeInboxApi.getStats(),
      ]);
      setInbox(inboxData);
      setStats(statsData);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, searchQuery, showUnreadOnly]);

  useEffect(() => { loadInbox(); }, [loadInbox]);

  const handleItemClick = useCallback(async (item: InboxItem) => {
    // Mark as read
    if (!item.is_read && item.client_id) {
      try {
        await intakeInboxApi.markAsRead(item.client_id);
      } catch { /* non-critical */ }
    }
    // Navigate to lead detail
    if (item.client_id) {
      onLeadClick({
        clientId: item.client_id,
        clientName: item.client_name || 'Unknown',
        status: item.is_read ? 'qualified' : 'lead',
        email: item.email,
        phone: item.phone,
        practiceArea: item.practice_area,
        createdAt: item.created_at,
        updatedAt: item.created_at,
        contactAttempts: 0,
        documentCount: 0,
      });
    }
  }, [onLeadClick]);

  if (loading && !inbox) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading inbox...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200">{error}</p>
        <button onClick={loadInbox} className="mt-2 text-sm text-red-600 underline hover:no-underline">Retry</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Stats bar */}
      {stats && (
        <div className="flex items-center gap-4 px-4 py-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-lex-accent">{stats.total}</span>
            <span className="text-xs text-gray-500">total</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.unread}</span>
            <span className="text-xs text-gray-500">unread</span>
          </div>
          {Object.entries(stats.by_source).slice(0, 4).map(([source, count]) => (
            <div key={source} className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{count}</span>
              <span className="text-xs text-gray-400">{source}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="relative flex-1 max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search inbox..."
            className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white w-full"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Sources</option>
          <option value="website">Website</option>
          <option value="email">Email</option>
          <option value="phone">Phone</option>
          <option value="referral">Referral</option>
          <option value="walk_in">Walk-in</option>
        </select>
        <label className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={showUnreadOnly} onChange={e => setShowUnreadOnly(e.target.checked)} className="rounded" />
          <span className="text-gray-600 dark:text-gray-400">Unread only</span>
        </label>
      </div>

      {/* Item list */}
      <div className="flex-1 overflow-auto">
        {(!inbox || inbox.items.length === 0) ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">No items in inbox</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {inbox.items.map(item => (
              <div
                key={item.id}
                onClick={() => handleItemClick(item)}
                className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                  !item.is_read ? 'bg-lex-accent-soft/50' : ''
                }`}
              >
                {/* Unread indicator */}
                <div className="pt-1.5">
                  {!item.is_read && (
                    <div className="w-2 h-2 rounded-full bg-lex-accent"></div>
                  )}
                  {item.is_read && <div className="w-2 h-2"></div>}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className={`text-sm font-medium ${!item.is_read ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'}`}>
                      {item.client_name || 'Unknown'}
                    </span>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${SOURCE_COLORS[item.source_badge] || SOURCE_COLORS.Other}`}>
                      {item.source_badge}
                    </span>
                    {item.priority === 'high' && (
                      <span className={`text-[10px] font-medium ${PRIORITY_COLORS.high}`}>HIGH</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.summary}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                    {item.email && <span>{item.email}</span>}
                    {item.phone && <span>{item.phone}</span>}
                    {item.practice_area && <span>{item.practice_area}</span>}
                  </div>
                </div>

                {/* Timestamp */}
                <span className="text-[10px] text-gray-400 whitespace-nowrap">
                  {new Date(item.created_at).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadInboxView;
